import { useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useCommons } from "../lib/store";
import { projectById, personById, activePledges } from "../lib/domain";

/*
 * Roots — navigation-per-page model.
 *
 * Every Roots page answers ONE question: "How did THIS person's branch
 * grow?" The centered person owns the page; their direct invitees are
 * placed around them; the person who invited them appears as a faded,
 * dashed ghost anchoring the page in context. Tapping any invitee
 * navigates to that invitee's own Roots page — this is navigation, not
 * expansion.
 *
 * Overflow rule: a page shows AT MOST `MAX_INDIVIDUAL` direct invitees
 * individually. When there are more, the smallest branches are folded
 * into a single dashed "+N more" chip. That chip is itself a navigation
 * target — tapping it either jumps to the one hidden sibling (if N=1)
 * or opens a sibling batch page (`?more=k`) centered on the same person,
 * showing the next k*MAX_INDIVIDUAL..k*MAX_INDIVIDUAL+MAX_INDIVIDUAL
 * direct invitees. Overflow always represents additional direct siblings
 * of the centered person — never descendants, never grandchildren.
 */

const CANVAS_SIZE = 400;
const CENTER = CANVAS_SIZE / 2;
const RING_RADIUS = 130;
const CENTER_R = 26;
const NODE_R = 22;
const MAX_INDIVIDUAL = 8;
// Non-founder focused pages fan children through a widening arc
// centered on the outward direction (opposite the incoming ghost
// parent). Small sibling groups keep the historical 160° fan; larger
// groups gain progressively more of the outward hemisphere, capped at
// 180° so children never grow back past the ghost hemisphere.
//
// N: visible children (individual invitees + optional overflow chip).
//
// N ≤ 3 : 160°   (unchanged from the prior fixed fan — no demonstrated
//                 problem at low N and preserves the "growth has a
//                 direction, not a lane" reading for simple branches)
// N = 4 : 165°
// N = 5 : 170°
// N = 6 : 175°
// N ≥ 7 : 180°   (cap — most of the outward hemisphere, never wraps)
//
// See the Roots fan-width visual experiment (Feb 2026) for the A/B/C
// comparison. Growth still continues away from where you came from —
// the ghost side stays intentionally empty even at the widest fan.
// Founder / steward pages remain full 360° and do not call this.
function outwardFanFor(N) {
  const deg = 160 + 5 * Math.max(0, Math.min(4, N - 3));
  return (deg * Math.PI) / 180;
}
// Legacy alias — the non-adaptive constant fan used before this
// experiment. Kept only for the ghost-limb chip continuity code paths
// that still reference the historical 160° value directly. The
// current-page layout math uses outwardFanFor(N) instead.
const OUTWARD_FAN = Math.PI * (160 / 180);

const SAGE = "#6E8E3F";
const SAGE_LIGHT = "#8FAE58";

function hash01(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return ((h >>> 0) % 10000) / 10000;
}

// ---------- data helpers ----------
function inviterFor(personId, invitations, projectId, stewardId) {
  const inv = invitations.find(
    (i) => i.projectId === projectId && i.inviteePersonId === personId,
  );
  // Structural rule: Founder = root. Invitation = edge.
  // A non-root participant without a recorded invitation is invalid
  // provenance — return null rather than silently attaching them to
  // the steward. Integrity tests (backend/tests/test_participation_
  // invariant.py) prevent this state from arising in practice.
  return inv?.inviterId || null;
}

function directInviteesOf(personId, invitations, committedIds, projectId, stewardId) {
  const out = new Set();
  // Committed people whose invitation lists this person as inviter.
  for (const inv of invitations) {
    if (inv.projectId !== projectId) continue;
    if (inv.inviterId !== personId) continue;
    if (!committedIds.has(inv.inviteePersonId)) continue;
    out.add(inv.inviteePersonId);
  }
  return Array.from(out);
}

// Count the total downstream branch under a given person (recursive fan).
function branchSize(personId, invitations, committedIds, projectId, stewardId, seen = new Set()) {
  if (seen.has(personId)) return 0;
  seen.add(personId);
  const kids = directInviteesOf(personId, invitations, committedIds, projectId, stewardId);
  let n = 0;
  for (const k of kids) n += 1 + branchSize(k, invitations, committedIds, projectId, stewardId, seen);
  return n;
}

// A stable seed for any invitation edge, symmetric so both endpoints
// produce the same curve. Walking Avery→Marcelo and then Marcelo→Avery
// draws the same limb from either direction, preserving branch identity.
function edgeSeed(a, b) {
  const [x, y] = a < b ? [a, b] : [b, a];
  return hash01(`edge:${x}::${y}`);
}
function edgeSwayDir(a, b) {
  return hash01(`edgeDir:${a < b ? a : b}::${a < b ? b : a}`) > 0.5 ? 1 : -1;
}

// ---------- orientation ----------
// Given the visible siblings and where the ghost parent sits (or null
// for a steward page), where does the sibling at `idx` land?
//
// Founder / steward page (ghostAngle == null):
//   • Full 360° fan.
//   • Largest branch anchored at 12 o'clock.
//   • Per-slot jitter ±22 % so the ring never snaps to a compass rose.
//
// Every focused non-founder page (ghostAngle != null):
//   • outAngle = ghostAngle + π  (direction away from the parent).
//   • Descendants fan through outwardFanFor(N) — 160° for N ≤ 3,
//     widening to 180° for N ≥ 7 — centered on outAngle. The
//     parent-side hemisphere stays intentionally empty.
//   • Same log-weighted / uniform-blended allocation within the fan.
//   • Per-slot jitter ±10 % — guarantees no child crosses back toward
//     the ghost hemisphere.
//
// Both the current page's placement and the recursive ghost-angle
// chain call this exact function, so orientation stays perfectly
// consistent as the visitor navigates in and out.
function arcAngleAt(items, ghostAngle, idx) {
  const N = items.length;
  if (N === 0) return -Math.PI / 2;
  const isFounderPage = ghostAngle === null || ghostAngle === undefined;
  // Log-weight keeps big subtrees pulling harder but never runaway.
  const rawW = items.map((it) => 1 + Math.log(1 + Math.max(0, it.weight || 0)));
  const rawSum = rawW.reduce((s, w) => s + w, 0) || 1;
  // Blend weighted allocation with uniform. Focused pages weight far
  // more heavily toward uniform (BLEND=0.78) so a single heavy branch
  // no longer eats an outsized slot at the fan edge while all the
  // small siblings squeeze together on the far side. Founder pages
  // keep the previous mix — largest-at-12-o'clock is a positive
  // property when there's a full 360° to work with.
  const BLEND = isFounderPage ? 0.5 : 0.78;
  const uniformShare = 1 / N;
  const weighted = rawW.map(
    (w) => BLEND * uniformShare + (1 - BLEND) * (w / rawSum),
  );
  // Per-slot width perturbation — deterministic ±25 % nudge of each
  // slot's own arc-width, then re-normalized so the fan is still
  // fully used. Two consecutive small siblings might end up one narrow
  // and one wider, breaking the "each rank gets a smooth-graded slot"
  // rhythm without changing sort order.
  const perturbed = items.map((it, i) => {
    const p = 0.75 + 0.5 * hash01((it.id || String(i)) + "|wperturb");
    return weighted[i] * p;
  });
  const perturbedSum = perturbed.reduce((s, x) => s + x, 0) || 1;
  const shares = perturbed.map((x) => x / perturbedSum);

  const totalArc = isFounderPage ? 2 * Math.PI : outwardFanFor(N);
  const firstArc = shares[0] * totalArc;
  const arcStart = isFounderPage
    ? -Math.PI / 2 - firstArc / 2
    : ghostAngle + Math.PI - totalArc / 2;

  let cursor = arcStart;
  for (let i = 0; i < idx; i++) cursor += shares[i] * totalArc;
  const myArc = shares[idx] * totalArc;
  const center = cursor + myArc / 2;
  // Slot walk-off (±22 %) + smaller inner jitter (±9-15 %) stay inside
  // the slot band but break the compass-rose regularity that pure
  // slot-center allocation produces.
  const walkFactor = 0.44;
  const walk = (hash01((items[idx].id || String(idx)) + "|walkoff") - 0.5) * myArc * walkFactor;
  const jitterFactor = isFounderPage ? 0.30 : 0.18;
  const jitter =
    (hash01((items[idx].id || String(idx)) + "|angle") - 0.5) * myArc * jitterFactor;
  return center + walk + jitter;
}

// Sorted list of a person's visible direct invitees (branch-size DESC).
// Same sort the render uses so idx lookups agree with visual placement.
function sortedInviteesOf(personId, data, committedIds, projectId, stewardId) {
  const kids = directInviteesOf(personId, data.invitations, committedIds, projectId, stewardId);
  return kids
    .map((pid) => ({
      pid,
      size: branchSize(pid, data.invitations, committedIds, projectId, stewardId),
    }))
    .sort((a, b) => b.size - a.size);
}

// Where should the parent ghost be placed on `personId`'s page? This is
// what preserves the mental map: we chase the invitation chain up to the
// steward, reproducing each layer's ACTUAL rendered layout — including
// pagination + overflow chip — so the direction the ghost sits is
// exactly opposite the direction the visitor walked to get here.
function ghostAngleFor(personId, data, committedIds, projectId, stewardId, seen = new Set()) {
  if (personId === stewardId) return null;
  if (seen.has(personId)) return null;
  seen.add(personId);
  const parentId = inviterFor(personId, data.invitations, projectId, stewardId);
  if (!parentId) return null;
  const grandGhost = ghostAngleFor(parentId, data, committedIds, projectId, stewardId, seen);
  const siblings = sortedInviteesOf(parentId, data, committedIds, projectId, stewardId);
  const idxInAll = siblings.findIndex((s) => s.pid === personId);
  if (idxInAll < 0) return null;
  // Reproduce the render's pagination + overflow slot exactly, so this
  // recursion's arcAngleAt call operates on the same slot set as the
  // actual layout on parent's page. If the person is compressed inside
  // the parent's +N overflow chip, anchor the ghost to the chip's slot
  // — that IS the direction they visually came from (the chip is the
  // compressed representation of the whole overflow set). This
  // preserves the invitation-genealogy → parent-ghost invariant even
  // when the child is reached without URL continuity params
  // (direct URL, bundle-page click-through, etc.).
  const shown = siblings.slice(0, MAX_INDIVIDUAL);
  const hiddenCount = siblings.length - MAX_INDIVIDUAL;
  const hasOverflow = hiddenCount > 0;
  const arcItems = [
    ...shown.map((s) => ({ id: "p-" + s.pid, weight: s.size })),
    ...(hasOverflow ? [{ id: "overflow", weight: hiddenCount }] : []),
  ];
  const slotIdx = idxInAll < MAX_INDIVIDUAL ? idxInAll : MAX_INDIVIDUAL;
  const angleFromParent = arcAngleAt(arcItems, grandGhost, slotIdx);
  return angleFromParent + Math.PI;
}

// ---------- branch geometry (kept from the earlier visual language) ----------

// Shift the "parent-side" endpoint of a branch `R` px toward the other
// endpoint. Used to disperse sibling departures along the parent-node
// circumference so multiple siblings emerge from visibly distinct
// points rather than all appearing to leave from a single hub.
// Direction-based, so nearby children keep nearby attachment points —
// no evenly-spaced ports. Symmetric across both endpoints of an edge
// (both endpoints shift toward the other endpoint by the same R), so
// page-to-page ghost reciprocity is preserved. See the Roots
// curvature-floor + departure-dispersion experiment (Feb 2026).
const DEPARTURE_R = 16;
function disperseFrom(fromX, fromY, towardX, towardY, R) {
  if (!R) return { x: fromX, y: fromY };
  const dx = towardX - fromX, dy = towardY - fromY;
  const d = Math.sqrt(dx * dx + dy * dy) || 1;
  return { x: fromX + (dx / d) * R, y: fromY + (dy / d) * R };
}

// Canonical cubic Bezier control-point parameters, shared by the
// tapered branch fill, its centerline, and the leaves that ride the
// same curve. `seed` is the per-edge deterministic seed in [0,1].
//
// The first control point (t1c, off1c) is deliberately pulled close
// to the start with a small perpendicular offset so the tangent at
// t=0 points nearly along the parent→child axis. This is the
// "early separation" rule (Feb 2026 sibling-branch experiment): when
// multiple siblings leave the same parent, each branch immediately
// begins bending toward its own child rather than sharing a departure
// corridor with its neighbors. The second control point (t2c, off2c)
// keeps the historical tip-side curvature, so branches remain
// visibly hand-grown rather than becoming straight lines.
//
// Both endpoints of any edge derive the same seed from `edgeSeed`, so
// this helper returns identical parameters on both sides and
// page-to-page ghost reciprocity is preserved.
function curveParams(seed) {
  return {
    t1c: 0.08 + seed * 0.08,
    t2c: 0.62 + seed * 0.14,
    off1cFactor: 0.10 + (1 - seed) * 0.10,
    off2cFactor: 0.10 + seed * 0.10,
  };
}

// Cubic Bezier tapered branch. `arcMag` is a per-edge multiplier applied
// to both control-point offsets so different branches can be nearly
// straight, mildly bowed, or deeply arced without changing the
// canonical Bezier structure.
//
// `isReversed`: canonical direction of an edge is lower-lex-id →
// higher-lex-id. Drawing from the higher-lex-id endpoint uses the
// canonical parameters swapped and mirrored (t1↔1−t2, off1↔off2), which
// combined with the caller's flipped swayDir and the naturally-flipped
// perpendicular produces the same physical curve in world space.
function taperedBranchPath(x1, y1, x2, y2, wBase, wTip, swayDir, seed = 0.5, isReversed = false, arcMag = 1.0) {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = dx / len, ny = dy / len; // along
  const px = -ny, py = nx;            // perpendicular (rotated +90°)
  // Canonical parameters (measured from the lower-lex-id endpoint).
  const cp = curveParams(seed);
  const t1c = cp.t1c;
  const t2c = cp.t2c;
  const off1c = cp.off1cFactor * len * arcMag;
  const off2c = cp.off2cFactor * len * arcMag;
  const t1 = isReversed ? (1 - t2c) : t1c;
  const t2 = isReversed ? (1 - t1c) : t2c;
  const off1 = (isReversed ? off2c : off1c) * swayDir;
  const off2 = (isReversed ? off1c : off2c) * swayDir;
  const c1x = x1 + nx * (t1 * len) + px * off1;
  const c1y = y1 + ny * (t1 * len) + py * off1;
  const c2x = x1 + nx * (t2 * len) + px * off2;
  const c2y = y1 + ny * (t2 * len) + py * off2;
  const wMid = (wBase * 0.65 + wTip * 0.35);
  const sL = { x: x1 + px * (wBase / 2), y: y1 + py * (wBase / 2) };
  const sR = { x: x1 - px * (wBase / 2), y: y1 - py * (wBase / 2) };
  const c1L = { x: c1x + px * (wMid / 2), y: c1y + py * (wMid / 2) };
  const c1R = { x: c1x - px * (wMid / 2), y: c1y - py * (wMid / 2) };
  const c2L = { x: c2x + px * (wTip / 2), y: c2y + py * (wTip / 2) };
  const c2R = { x: c2x - px * (wTip / 2), y: c2y - py * (wTip / 2) };
  const eL = { x: x2 + px * (wTip / 2), y: y2 + py * (wTip / 2) };
  const eR = { x: x2 - px * (wTip / 2), y: y2 - py * (wTip / 2) };
  return (
    `M ${sL.x} ${sL.y} ` +
    `C ${c1L.x} ${c1L.y}, ${c2L.x} ${c2L.y}, ${eL.x} ${eL.y} ` +
    `L ${eR.x} ${eR.y} ` +
    `C ${c2R.x} ${c2R.y}, ${c1R.x} ${c1R.y}, ${sR.x} ${sR.y} ` +
    `Z`
  );
}

function centerlinePath(x1, y1, x2, y2, swayDir, seed, isReversed = false, arcMag = 1.0) {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = dx / len, ny = dy / len;
  const px = -ny, py = nx;
  const cp = curveParams(seed);
  const t1c = cp.t1c;
  const t2c = cp.t2c;
  const off1c = cp.off1cFactor * len * arcMag;
  const off2c = cp.off2cFactor * len * arcMag;
  const t1 = isReversed ? (1 - t2c) : t1c;
  const t2 = isReversed ? (1 - t1c) : t2c;
  const off1 = (isReversed ? off2c : off1c) * swayDir;
  const off2 = (isReversed ? off1c : off2c) * swayDir;
  const c1x = x1 + nx * (t1 * len) + px * off1;
  const c1y = y1 + ny * (t1 * len) + py * off1;
  const c2x = x1 + nx * (t2 * len) + px * off2;
  const c2y = y1 + ny * (t2 * len) + py * off2;
  return `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;
}

function leafShape(size) {
  const s = size;
  return `M 0 0 Q ${s * 0.6} ${-s * 0.7}, ${s * 1.6} 0 Q ${s * 0.6} ${s * 0.7}, 0 0 Z`;
}

function branchLeaves(x1, y1, x2, y2, swayDir, seed, weight, isReversed = false, arcMag = 1.0) {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = dx / len, ny = dy / len;
  const px = -ny, py = nx;
  // Match the branch's canonical cubic geometry so leaves ride the
  // actual drawn curve regardless of which endpoint we started from.
  const cp = curveParams(seed);
  const t1c = cp.t1c;
  const t2c = cp.t2c;
  const off1c = cp.off1cFactor * len * arcMag;
  const off2c = cp.off2cFactor * len * arcMag;
  const t1 = isReversed ? (1 - t2c) : t1c;
  const t2 = isReversed ? (1 - t1c) : t2c;
  const off1 = (isReversed ? off2c : off1c) * swayDir;
  const off2 = (isReversed ? off1c : off2c) * swayDir;
  const c1x = x1 + nx * (t1 * len) + px * off1;
  const c1y = y1 + ny * (t1 * len) + py * off1;
  const c2x = x1 + nx * (t2 * len) + px * off2;
  const c2y = y1 + ny * (t2 * len) + py * off2;
  const vitality = Math.min(4, Math.floor(Math.log(1 + weight) * 1.4));
  const count = 3 + vitality;
  const out = [];
  for (let i = 0; i < count; i++) {
    const t = 0.28 + (i / count) * 0.6;
    // Cubic Bezier point at parameter t
    const u = 1 - t;
    const bx = u * u * u * x1 + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * x2;
    const by = u * u * u * y1 + 3 * u * u * t * c1y + 3 * u * t * t * c2y + t * t * t * y2;
    // Tangent at t
    const tx = 3 * u * u * (c1x - x1) + 6 * u * t * (c2x - c1x) + 3 * t * t * (x2 - c2x);
    const ty = 3 * u * u * (c1y - y1) + 6 * u * t * (c2y - c1y) + 3 * t * t * (y2 - c2y);
    const ang = Math.atan2(ty, tx);
    const side = (i % 2 === 0 ? 1 : -1) * swayDir;
    const nudge = 3 + hash01(`${seed}-${i}`) * 3.5;
    out.push({
      x: bx + -Math.sin(ang) * side * nudge,
      y: by + Math.cos(ang) * side * nudge,
      rot: ang + side * (Math.PI / 3),
      size: 3.2 + hash01(`${seed}-${i}s`) * 2,
      opacity: 0.45 + hash01(`${seed}-${i}o`) * 0.24,
    });
  }
  return out;
}

function twigsPastNode(nodeX, nodeY, outAngle, seed, weight, nodeR) {
  const twigs = [];
  const count = 2 + Math.min(2, Math.floor(Math.log(1 + weight) * 0.9));
  for (let i = 0; i < count; i++) {
    const s = hash01(`${seed}:tw:${i}`);
    const spread = outAngle + ((i - (count - 1) / 2) / count) * 0.9 + (s - 0.5) * 0.3;
    const startX = nodeX + Math.cos(spread) * (nodeR + 2);
    const startY = nodeY + Math.sin(spread) * (nodeR + 2);
    const len = 12 + s * 10;
    const endX = startX + Math.cos(spread) * len;
    const endY = startY + Math.sin(spread) * len;
    const perpX = -Math.sin(spread);
    const perpY = Math.cos(spread);
    const curl = (s - 0.5) * 7;
    const cx = (startX + endX) / 2 + perpX * curl;
    const cy = (startY + endY) / 2 + perpY * curl;
    twigs.push({
      d: `M ${startX} ${startY} Q ${cx} ${cy}, ${endX} ${endY}`,
      tipX: endX,
      tipY: endY,
      tipAngle: spread,
    });
  }
  return twigs;
}

// ---------- component ----------
export default function Roots() {
  const { id, personId: personIdParam } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const { data, user } = useCommons();

  // Roots is a focused vertical navigation context inside a project.
  // Entering Roots pushes exactly one history entry; everything the
  // visitor does INSIDE Roots (walking up, drilling into a bundle,
  // following an invitee) replaces that single entry rather than piling
  // new entries on top. That way the browser back button never lands
  // the visitor "somewhere in the middle of Roots" — leaving Roots
  // pops back to whatever pushed Roots (typically the Project page).
  //
  // `canPopExit` remembers whether Roots was pushed on top of prior
  // in-app history (`window.history.state.idx > 0` at first mount).
  // If so, exit uses nav(-1) to actually pop the Roots slot. Otherwise
  // (direct link, refresh) exit falls back to replacing with the
  // canonical Project URL.
  const canPopExitRef = useRef(null);
  if (canPopExitRef.current === null) {
    const idx =
      typeof window !== "undefined" && window.history?.state?.idx;
    canPopExitRef.current = typeof idx === "number" && idx > 0;
  }

  // `?more=k` — an overflow batch page. Same centered person, but the
  // visible invitees are the k-th batch of MAX_INDIVIDUAL siblings by
  // branch-size DESC (k=0 is the base page and equivalent to no param).
  //
  // `?fa=…&fd=…&fs=…` — visual continuity from a previous page's
  // overflow chip. fa = chip's angle (from prev page's center), fd =
  // its sway direction (+1|-1), fs = its sway seed. When present, the
  // destination page anchors the parent ghost to exactly that direction
  // and re-uses the same curve seed, so navigating through a chip feels
  // like continuing down the same limb rather than teleporting into a
  // fresh radial layout.
  const searchParams = new URLSearchParams(location.search);
  const moreLevel = Math.max(
    0,
    parseInt(searchParams.get("more") || "0", 10) || 0,
  );
  const fromAngleParam = searchParams.get("fa");
  const fromDirParam = searchParams.get("fd");
  const fromSeedParam = searchParams.get("fs");
  const fromMagParam = searchParams.get("fm");
  const fromAngle =
    fromAngleParam !== null && Number.isFinite(parseFloat(fromAngleParam))
      ? parseFloat(fromAngleParam)
      : null;
  const fromDir = fromDirParam === "1" ? 1 : fromDirParam === "-1" ? -1 : null;
  const fromSeed =
    fromSeedParam !== null && Number.isFinite(parseFloat(fromSeedParam))
      ? parseFloat(fromSeedParam)
      : null;
  const fromMag =
    fromMagParam !== null && Number.isFinite(parseFloat(fromMagParam))
      ? parseFloat(fromMagParam)
      : null;
  const hasContinuity = fromAngle !== null;

  const project = useMemo(
    () => (data && id ? projectById(data.projects, id) : undefined),
    [data, id],
  );
  const stewardId = project?.stewardId;
  // The centered person defaults to the project's steward. If a personId
  // is present in the URL and is committed to the project, we center on
  // them instead. Anything unknown falls back to the steward.
  const centeredId = personIdParam || stewardId;

  const committedIds = useMemo(() => {
    if (!project || !data) return new Set();
    return new Set(activePledges(project.id, data.pledges).map((p) => p.personId));
  }, [project, data]);

  const centeredPerson = data ? personById(data.people, centeredId) : null;
  const isSteward = centeredId === stewardId;

  const parentId = useMemo(() => {
    if (!project || !data || isSteward) return null;
    return inviterFor(centeredId, data.invitations, project.id, stewardId);
  }, [project, data, centeredId, isSteward, stewardId]);
  const parentPerson = parentId ? personById(data.people, parentId) : null;

  const allInvitees = useMemo(() => {
    if (!project || !data) return [];
    const kids = directInviteesOf(centeredId, data.invitations, committedIds, project.id, stewardId);
    // Order by branch size DESC so the busiest branch anchors the top.
    return kids
      .map((pid) => ({
        personId: pid,
        person: personById(data.people, pid),
        size: branchSize(pid, data.invitations, committedIds, project.id, stewardId),
      }))
      .sort((a, b) => b.size - a.size);
  }, [project, data, centeredId, committedIds, stewardId]);

  if (!data) return <div className="c-load">Loading…</div>;
  if (!project || !centeredPerson) return <div className="c-load">Not found.</div>;

  // Batch this page's visible invitees. moreLevel=0 → top MAX_INDIVIDUAL
  // by branch size. moreLevel=k → the k-th batch of MAX_INDIVIDUAL after
  // that. Overflow ONLY fires when the batch itself doesn't fit — never
  // when everyone can be shown individually.
  const startIdx = moreLevel * MAX_INDIVIDUAL;
  const batch = allInvitees.slice(startIdx);
  const hasOverflow = batch.length > MAX_INDIVIDUAL;
  const shown = hasOverflow ? batch.slice(0, MAX_INDIVIDUAL) : batch;
  const hiddenInvitees = hasOverflow ? batch.slice(MAX_INDIVIDUAL) : [];
  const hiddenCount = hiddenInvitees.length;

  // ---------- layout ----------
  // Ghost angle. When arriving through an overflow chip (`?fa=…`), we
  // anchor the ghost to fromAngle + π — the OPPOSITE endpoint of the
  // same limb that the chip occupied on the previous page. Together
  // with re-using the chip's sway seed (with direction flipped, since
  // we're drawing the same limb in reverse) this preserves limb
  // identity, not merely quadrant: same branch, new vantage point.
  //
  // Otherwise (a page reached by normal person navigation) the ghost
  // angle is derived from the invitation chain via ghostAngleFor,
  // which recursively reproduces each layer's layout so the invitation
  // parent stays exactly where the visitor last saw them.
  //
  // On a bundle page (moreLevel > 0), the ghost identity is the URL's
  // centered person (Avery). On a normal page, it's the invitation
  // parent.
  const isBundleView = moreLevel > 0;
  const visualParentPerson = isBundleView ? centeredPerson : parentPerson;
  const visualParentId = isBundleView ? centeredId : parentId;
  let ghostAngle = null;
  if (hasContinuity) {
    ghostAngle = fromAngle + Math.PI;
  } else if (visualParentPerson) {
    // Direct URL access to a bundle page (no continuity params) is a
    // rare bookmark case; anchor the ghost at 12 o'clock so the batch
    // fans downward — an unambiguous "back to Avery is up" cue.
    ghostAngle = isBundleView
      ? -Math.PI / 2
      : ghostAngleFor(centeredId, data, committedIds, project.id, stewardId);
  }

  const items = [];
  const arcItems = [
    ...shown.map((inv) => ({ id: `p-${inv.personId}`, weight: inv.size })),
    ...(hasOverflow ? [{ id: "overflow", weight: hiddenCount }] : []),
  ];

  // Per-branch radius classes — deterministic short/medium/long buckets
  // so the silhouette breaks the "everything on one invisible ring"
  // feel. Length is aesthetic, not a second encoding of downstream size.
  // Range 103-178 px keeps everything mobile-safe on a 400 px canvas
  // with ~44 px node + label height budget.
  const radiusFor = (id) => {
    const h = hash01(id + "|len");
    const cls =
      h < 0.15 ? 108
      : h < 0.35 ? 122
      : h < 0.72 ? 138
      : h < 0.90 ? 158
      : 172;
    return cls + (hash01(id + "|rjit") - 0.5) * 10;
  };
  // Deterministic sway direction and strength seed per branch.
  const swayDirFor = (id) => (hash01(id + "|sway") > 0.5 ? 1 : -1);
  const swaySeedFor = (id) => hash01(id + "|swayseed");
  // Curvature class per edge — most limbs are mildly bowed, some
  // nearly straight, a few take a deeper arc. Deterministic per seed.
  //
  // A minimum-curvature floor is applied to the "nearly straight"
  // class so no branch ever renders as a dead-straight line. The
  // mild-bow (1.00) and deeper-arc (1.75) classes are already above
  // the floor and therefore unchanged. Curvature still varies visibly
  // from branch to branch — this is a minimum gesture, not a global
  // increase. See the Roots curvature-floor experiment (Feb 2026).
  const CURVATURE_FLOOR = 0.85;
  const arcMagFor = (seed) => {
    const c = hash01(seed + "|arc");
    if (c < 0.20) return CURVATURE_FLOOR;   // nearly straight, lifted off the floor
    if (c < 0.75) return 1.00;              // mild bow (current baseline)
    return 1.75;                            // deeper arc
  };
  // Small helper: signed shortest angular difference in [-π, π].
  const angDelta = (a, b) => {
    let d = a - b;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return d;
  };
  // Spatial sway bias, computed against the canonical drawer's fan
  // (both endpoints of an edge derive the same value). If the canonical
  // drawer is the steward or on a bundle page, fanCenter is null and
  // the base hash direction is used unchanged. Otherwise: the further
  // the drawer's slot sits from the fan's center, the more likely the
  // sway direction bows outward into the parent-side hemisphere.
  const canonicalSway = (edgeSeedVal, drawerSlot, drawerFanCenter) => {
    const base = hash01(edgeSeedVal + "|d") > 0.5 ? 1 : -1;
    if (drawerFanCenter === null || drawerFanCenter === undefined) return base;
    const slotFrac = angDelta(drawerSlot, drawerFanCenter) / (OUTWARD_FAN / 2);
    const clamped = Math.max(-1, Math.min(1, slotFrac));
    const biasProb = 0.4 * Math.abs(clamped);
    if (hash01(edgeSeedVal + "|bias") < biasProb) {
      return clamped > 0 ? 1 : -1;
    }
    return base;
  };
  // Fan center for the CURRENT page (used when we are the canonical
  // drawer for one of our outgoing edges).
  const ourFanCenter = isSteward || isBundleView
    ? null
    : (ghostAngle !== null ? ghostAngle + Math.PI : null);

  // Bundle view: a `?more=k` page is UI pagination through additional
  // children of the same real parent. The visual center is an anonymous
  // "expanded bundle" placeholder — not a person, not a navigation
  // target, not part of the invitation graph. The URL's centered
  // person (Avery) is drawn as the ghost of this bundle, at the
  // OPPOSITE endpoint of the same limb that the chip occupied on the
  // previous page (fromAngle + π), so the visitor feels like they
  // travelled along that exact limb and are looking backward at Avery
  // from its other end. (`isBundleView`, `visualParentPerson`,
  // `visualParentId` are declared once above where ghostAngle is set.)

  shown.forEach((inv, i) => {
    const id = `p-${inv.personId}`;
    const angle = arcAngleAt(arcItems, ghostAngle, i);
    const r = radiusFor(id);
    items.push({
      kind: "invitee",
      id,
      personId: inv.personId,
      person: inv.person,
      size: inv.size,
      angle,
      x: CENTER + Math.cos(angle) * r,
      y: CENTER + Math.sin(angle) * r,
    });
  });
  if (hasOverflow) {
    const i = shown.length;
    // Project + centered-person + more-level scoped id so the +more
    // chip's curvature/length/sway are unique to this vantage point,
    // not a single "overflow" stencil reused everywhere.
    const id = `overflow-${project.id}-${centeredId}-${moreLevel}`;
    const angle = arcAngleAt(arcItems, ghostAngle, i);
    const r = radiusFor(id);
    items.push({
      kind: "overflow",
      id,
      angle,
      x: CENTER + Math.cos(angle) * r,
      y: CENTER + Math.sin(angle) * r,
      count: hiddenCount,
    });
  }

  // ---------- node / label protection (two separate systems) ----------
  //
  // (A) Node geometry protection — only run when two node circles
  //     ACTUALLY overlap. Push the lower-branch-size node tangentially
  //     into nearby free space by the minimum amount needed to clear
  //     the collision (capped at ~22 px). Never triggered by label
  //     proximity alone.
  //
  // (B) Adaptive label placement — the name + downstream-count package
  //     is annotation, not tree geometry. It can move to any of five
  //     deterministic candidate positions around its own node:
  //         below | below-outward | right | left | above
  //     Default remains "below" so an uncrowded tree renders exactly
  //     as it does today. Alternate positions are chosen only when the
  //     default meaningfully collides with another label, a node
  //     circle, or the canvas edge.
  //
  // (Bundle special) — if the visitor is on a bundle page, the
  // immovable centered-person replica sits at (CENTER, CENTER). Any
  // child whose class-picked radius lands its circle on top of that
  // replica gets its radius bumped up one class so the tree reads.

  // Bundle-center relaxation ------------------------------------------
  if (isBundleView) {
    const BUNDLE_CLEAR = 2 * CENTER_R + NODE_R + 6;
    for (const it of items) {
      const r = Math.hypot(it.x - CENTER, it.y - CENTER);
      if (r < BUNDLE_CLEAR) {
        const newR = Math.min(190, BUNDLE_CLEAR + 6);
        it.x = CENTER + Math.cos(it.angle) * newR;
        it.y = CENTER + Math.sin(it.angle) * newR;
      }
    }
  }

  // Node-circle relaxation --------------------------------------------
  const nodesMoved = [];
  {
    const CIRCLE_MARGIN = 4;
    const MAX_TANG_PUSH = 22;
    // One pass — check every unordered pair. Deterministic order: by
    // the arc-index that produced the item, so ties resolve stably.
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i];
        const b = items[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy);
        const minDist = 2 * NODE_R + CIRCLE_MARGIN;
        if (dist >= minDist) continue;
        const aWeight = a.kind === "overflow" ? a.count : (a.size || 0);
        const bWeight = b.kind === "overflow" ? b.count : (b.size || 0);
        const pushB = bWeight < aWeight || (bWeight === aWeight && j > i);
        const target = pushB ? b : a;
        // Tangent to the target's radial direction, pointed away from
        // the other node. Purely tangential motion keeps the node on
        // its own radial "layer" so length hierarchy remains readable.
        const tx = -Math.sin(target.angle);
        const ty = Math.cos(target.angle);
        const away = pushB ? { x: dx, y: dy } : { x: -dx, y: -dy };
        const sign = tx * away.x + ty * away.y >= 0 ? 1 : -1;
        const push = Math.min(MAX_TANG_PUSH, minDist - dist + 2);
        target.x += tx * sign * push;
        target.y += ty * sign * push;
        nodesMoved.push({ id: target.id, push, reason: `circle overlap with ${(pushB ? a : b).id}` });
      }
    }
  }

  // Adaptive label placement -------------------------------------------
  // Estimate the (name + optional +N) package's bounding rectangle so
  // we can test candidate positions against real geometry rather than
  // an angular approximation. All measurements deterministic.
  const LABEL_NAME_H = 16;
  const LABEL_COUNT_H = 14;
  const LABEL_MAX_W = 92;
  const LABEL_CHAR_W = 6.8;   // 13 px bold, empirical
  const LABEL_GAP = 4;
  const packSize = (it) => {
    const label = it.kind === "overflow" ? "more" : (it.person?.name || "");
    const hasCount = it.kind !== "overflow" && (it.size || 0) > 0;
    const w = Math.min(LABEL_MAX_W, Math.max(18, label.length * LABEL_CHAR_W + 6));
    const h = LABEL_NAME_H + (hasCount ? LABEL_COUNT_H : 0);
    return { w, h };
  };
  const packRectAt = (it, pos, size) => {
    const cx = it.x;
    const cy = it.y;
    const outX = Math.cos(it.angle);
    const outY = Math.sin(it.angle);
    if (pos === "below") {
      return { x: cx - size.w / 2, y: cy + NODE_R + LABEL_GAP, w: size.w, h: size.h };
    }
    if (pos === "below-outward") {
      return {
        x: cx + outX * 14 - size.w / 2,
        y: cy + NODE_R + LABEL_GAP + outY * 14,
        w: size.w, h: size.h,
      };
    }
    if (pos === "right") {
      return { x: cx + NODE_R + 6, y: cy - size.h / 2, w: size.w, h: size.h };
    }
    if (pos === "left") {
      return { x: cx - NODE_R - 6 - size.w, y: cy - size.h / 2, w: size.w, h: size.h };
    }
    // above
    return { x: cx - size.w / 2, y: cy - NODE_R - LABEL_GAP - size.h, w: size.w, h: size.h };
  };
  const rectOverlap = (a, b) => {
    const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
    const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
    return ix * iy;
  };
  const CANDIDATES = ["below", "below-outward", "right", "left", "above"];
  const PREFERENCE_COST = { "below": 0, "below-outward": 3, "right": 8, "left": 8, "above": 16 };
  // Process in DESC branch-size order so bigger branches "claim" their
  // preferred spot first and smaller branches adapt to what's left.
  // Ties broken by original arc index.
  const processOrder = items
    .map((it, i) => ({ it, i, w: it.kind === "overflow" ? it.count : (it.size || 0) }))
    .sort((a, b) => (b.w - a.w) || (a.i - b.i));
  const placedRects = [];
  const computeScore = (rect, pos, it) => {
    let score = PREFERENCE_COST[pos];
    // Off-canvas penalty
    const overflowLeft   = Math.max(0, 6 - rect.x);
    const overflowRight  = Math.max(0, rect.x + rect.w - (CANVAS_SIZE - 6));
    const overflowTop    = Math.max(0, 6 - rect.y);
    const overflowBottom = Math.max(0, rect.y + rect.h - (CANVAS_SIZE - 6));
    score += (overflowLeft + overflowRight + overflowTop + overflowBottom) * 4;
    // Overlap with other node circles (approximate each as a square).
    for (const other of items) {
      if (other === it) continue;
      const nodeRect = {
        x: other.x - NODE_R, y: other.y - NODE_R,
        w: NODE_R * 2, h: NODE_R * 2,
      };
      score += rectOverlap(rect, nodeRect) * 0.10;   // per sq-px
    }
    // Overlap with the bundle-center replica.
    if (isBundleView) {
      const bundleRect = { x: CENTER - CENTER_R, y: CENTER - CENTER_R, w: CENTER_R * 2, h: CENTER_R * 2 };
      score += rectOverlap(rect, bundleRect) * 0.10;
    }
    // Overlap with already-placed label rectangles.
    for (const placed of placedRects) {
      score += rectOverlap(rect, placed) * 0.06;
    }
    // Small penalty for a branch crossing near the label — approximate
    // by checking if any OTHER node's radial line passes near this
    // rect. Not a disqualifier; some proximity is fine.
    for (const other of items) {
      if (other === it) continue;
      for (let t = 0.35; t <= 0.85; t += 0.25) {
        const px = CENTER + (other.x - CENTER) * t;
        const py = CENTER + (other.y - CENTER) * t;
        if (px >= rect.x && px <= rect.x + rect.w &&
            py >= rect.y && py <= rect.y + rect.h) {
          score += 4;
          break;
        }
      }
    }
    return score;
  };
  const BELOW_KEEP_THRESHOLD = 5;   // if below scores under this, keep it
  for (const entry of processOrder) {
    const it = entry.it;
    const size = packSize(it);
    // Always start with "below" and only try alternatives if it collides.
    const belowRect = packRectAt(it, "below", size);
    const belowScore = computeScore(belowRect, "below", it);
    if (belowScore <= BELOW_KEEP_THRESHOLD) {
      it.labelPos = "below";
      it.labelBounds = belowRect;
      placedRects.push(belowRect);
      continue;
    }
    // Search alternatives.
    let bestPos = "below";
    let bestScore = belowScore;
    let bestRect = belowRect;
    for (const pos of CANDIDATES) {
      if (pos === "below") continue;
      const rect = packRectAt(it, pos, size);
      const score = computeScore(rect, pos, it);
      if (score < bestScore) {
        bestScore = score;
        bestPos = pos;
        bestRect = rect;
      }
    }
    it.labelPos = bestPos;
    it.labelBounds = bestRect;
    // For below-outward, store the inline translate the renderer will apply.
    if (bestPos === "below-outward") {
      it.labelOffsetX = Math.cos(it.angle) * 14;
      it.labelOffsetY = Math.sin(it.angle) * 14;
    } else {
      it.labelOffsetX = 0;
      it.labelOffsetY = 0;
    }
    placedRects.push(bestRect);
  }
  // parentPlaced — the dashed ghost node.
  //   • Normal page: ghost is the real invitation parent (Sarah). Its
  //     distance from the current center matches the length the same
  //     limb had on the parent's page — i.e. radiusFor("p-" + centeredId),
  //     which is the radius the current page's centered person occupied
  //     when they were rendered as one of the parent's invitees.
  //   • Bundle page: ghost is the URL's centered person (Avery) — an
  //     anchor, not a real edge — so we keep the vanilla RING_RADIUS.
  const parentGhostRadius = isBundleView
    ? RING_RADIUS
    : radiusFor("p-" + centeredId);
  const parentPlaced =
    visualParentPerson && ghostAngle !== null
      ? {
          kind: "parent",
          id: `parent-${visualParentId}`,
          personId: visualParentId,
          person: visualParentPerson,
          angle: ghostAngle,
          x: CENTER + Math.cos(ghostAngle) * parentGhostRadius,
          y: CENTER + Math.sin(ghostAngle) * parentGhostRadius,
        }
      : null;

  // Branches from center to each invitee/overflow.
  // Sway direction is biased outward at fan edges (see canonicalSway).
  // The bias is computed from the invitation parent's own fan-info so
  // both endpoints of any edge derive the same canonical value —
  // combined with the lex-based sign flip, both pages produce the same
  // physical curve in world space (Pass A reciprocity).
  const branches = items.map((it) => {
    const isOverflow = it.kind === "overflow";
    let swayDir;
    let swaySeed;
    let arcMag;
    let isReversed;
    if (isOverflow) {
      swayDir = swayDirFor(it.id);
      swaySeed = swaySeedFor(it.id);
      arcMag = arcMagFor(it.id);
      isReversed = false;
    } else {
      const edgeSeedVal = edgeSeed(centeredId, it.personId);
      // Invitation parent for this edge = us (centeredId). Slot = angle
      // of the child on our page. Fan-center = our own fan.
      const parentSlot = it.angle;
      const parentFanCenter = ourFanCenter;
      const cSway = canonicalSway(edgeSeedVal, parentSlot, parentFanCenter);
      swayDir = cSway * (centeredId < it.personId ? 1 : -1);
      swaySeed = edgeSeedVal;
      arcMag = arcMagFor(edgeSeedVal);
      isReversed = centeredId > it.personId;
    }
    const weight = isOverflow ? it.count : it.size;
    // Restrained scaling — branch magnitude should read as a *whisper*,
    // not as visual authority. On a large tree (e.g. Halloween's Bex +327
    // page) the descendant counts of +167 / +125 / +124 / +93 vary by
    // roughly 80×, but the stroke channel is deliberately compressed to
    // a ~2× range so that no branch ever renders as a structural trunk.
    // The explicit "+N" label remains the primary, precise indication of
    // magnitude; stroke width is a secondary hint that says
    // "somewhat more abundant here" without saying "more powerful here".
    // Hard cap keeps person nodes visually stronger than any connection.
    // See the Roots stroke-width visual experiment (Feb 2026) for the
    // A/B/C comparison that landed us on this mapping.
    const wBase = Math.max(2.0, Math.min(4.0, 2.0 + 0.60 * Math.log10(1 + weight)));
    const wTip = Math.max(1.4, wBase * 0.50);
    // Departure-dispersion: shift the parent-side endpoint slightly
    // toward this child's direction so siblings emerge from distinct
    // points on the parent circumference.
    const dep = disperseFrom(CENTER, CENTER, it.x, it.y, DEPARTURE_R);
    return {
      id: it.id,
      d: taperedBranchPath(dep.x, dep.y, it.x, it.y, wBase, wTip, swayDir, swaySeed, isReversed, arcMag),
      swayDir,
      swaySeed,
      leaves: branchLeaves(dep.x, dep.y, it.x, it.y, swayDir, swaySeed, weight, isReversed, arcMag),
      x2: it.x,
      y2: it.y,
      weight,
    };
  });

  // Ghost limb — the dashed connection back to the parent. Uses the
  // *same* edge seed + canonical sway (biased from the parent's own
  // fan-info) as the parent's outgoing branch to us, so both endpoints
  // produce the same physical curve in world space.
  //
  // When we arrived via an overflow chip, we instead re-use the chip's
  // seed (with sway direction flipped, since we're drawing the same
  // limb in reverse) so the ghost mirrors the exact curve of the chip
  // on the previous page.
  const grandGhostForBias =
    !hasContinuity && !isBundleView && parentPlaced && visualParentId && visualParentId !== stewardId
      ? ghostAngleFor(visualParentId, data, committedIds, project.id, stewardId)
      : null;
  const ghostSwayDir = (() => {
    if (hasContinuity && fromDir !== null) return -fromDir;
    if (!(parentPlaced && visualParentId)) return 1;
    if (isBundleView) {
      // Bundle anchor — synthetic edge, no reciprocity to preserve.
      return edgeSwayDir(centeredId, visualParentId) * (centeredId < visualParentId ? 1 : -1);
    }
    const edgeSeedVal = edgeSeed(centeredId, visualParentId);
    // Invitation parent = visualParentId (they invited us). We need the
    // parent's own fan info as it would appear on THEIR page.
    const parentSlot = ghostAngle - Math.PI;   // where we sat on parent's page
    const parentFanCenter = grandGhostForBias === null
      ? null
      : grandGhostForBias + Math.PI;
    const cSway = canonicalSway(edgeSeedVal, parentSlot, parentFanCenter);
    return cSway * (visualParentId < centeredId ? 1 : -1);
  })();
  const ghostSwaySeed = hasContinuity && fromSeed !== null
    ? fromSeed
    : parentPlaced && visualParentId
      ? edgeSeed(centeredId, visualParentId)
      : 0;
  // Canonical-endpoint remap for the ghost — same rule as taperedBranchPath.
  // Skip for the chip-continuity path and for bundle anchors.
  const ghostIsReversed = hasContinuity || isBundleView
    ? false
    : parentPlaced && visualParentId
      ? centeredId > visualParentId
      : false;
  const ghostArcMag = hasContinuity && fromMag !== null
    ? fromMag
    : parentPlaced && visualParentId
      ? arcMagFor(edgeSeed(centeredId, visualParentId))
      : 1.0;
  // Departure-dispersion for the ghost limb — shift the parent's
  // ghost endpoint toward CENTER by the same R so both endpoints of
  // any edge agree on their attachment points (page-to-page ghost
  // reciprocity preserved).
  const ghostDep = parentPlaced
    ? disperseFrom(parentPlaced.x, parentPlaced.y, CENTER, CENTER, DEPARTURE_R)
    : null;
  const ghostPath = parentPlaced
    ? centerlinePath(CENTER, CENTER, ghostDep.x, ghostDep.y, ghostSwayDir, ghostSwaySeed, ghostIsReversed, ghostArcMag)
    : null;

  // Terminal twigs past every invitee/overflow (there's nothing further to
  // render — this page shows only direct invitees).
  const twigs = [];
  for (const it of items) {
    const seed = hash01(it.id + "|twig");
    const weight = it.kind === "invitee" ? it.size : it.count;
    twigs.push(...twigsPastNode(it.x, it.y, it.angle, seed, weight, NODE_R));
  }

  // ---------- interactions ----------
  // Every intra-Roots destination REPLACES the current Roots slot so
  // the visitor's browser history stays anchored to whatever pushed
  // Roots. Deep exploration within Roots is purely vertical and should
  // never leave a trail of horizontal-looking history entries.
  const navInRoots = (path) => nav(path, { replace: true });

  const goToInvitee = (pid) => navInRoots(`/roots/${project.id}/${pid}`);

  // Shared navigation model: what does "one step upward in the Roots
  // structure" mean from the current page? Both the back arrow and the
  // ghost node consult this helper so they stay behaviorally aligned
  // without either being implemented in terms of the other.
  //
  //   • Bundle page (moreLevel > 0): collapse one bundle level. Never
  //     exits Roots — a nested bundle first collapses to its outer
  //     bundle, then to the centered person.
  //   • Normal person page: walk up the invitation chain to the
  //     inviter. Never exits Roots as long as a parent exists.
  //   • Project root (no bundle, no parent): the only case that leaves
  //     Roots. Returns the Project Detail page.
  //
  // Returns `{ kind, path }`. Consumers decide whether they act on
  // "exit" (back arrow does; ghost never sees it because no ghost is
  // rendered at the project root).
  const previousRootsState = () => {
    if (moreLevel > 0) {
      const base = isSteward
        ? `/roots/${project.id}`
        : `/roots/${project.id}/${centeredId}`;
      return {
        kind: "bundle-collapse",
        path: moreLevel === 1 ? base : `${base}?more=${moreLevel - 1}`,
      };
    }
    if (parentId) {
      // Prefer the canonical steward URL when walking up to the steward,
      // matching how bundle-collapse already picks a base URL.
      const path =
        parentId === stewardId
          ? `/roots/${project.id}`
          : `/roots/${project.id}/${parentId}`;
      return { kind: "walk-up", path };
    }
    return { kind: "exit", path: `/project/${project.id}` };
  };

  const goToParent = () => {
    const prev = previousRootsState();
    // The ghost is only rendered when there's a parent to walk back to
    // (bundle-collapse or walk-up), never at the project root — so it
    // never encounters the "exit" branch. Both remaining branches stay
    // inside Roots and use the shared replace-navigation helper.
    if (prev.kind === "exit") {
      exitRoots(prev.path);
      return;
    }
    navInRoots(prev.path);
  };

  const goBack = () => {
    const prev = previousRootsState();
    if (prev.kind === "exit") {
      exitRoots(prev.path);
      return;
    }
    navInRoots(prev.path);
  };

  // Leaving Roots. If Roots was pushed onto prior in-app history, pop
  // it (`nav(-1)`) so the visitor lands on the page that pushed Roots
  // and can continue up the app hierarchy naturally
  // (Home → Category → Project → Roots → back → Project → back →
  // Category). Otherwise (direct link, refresh) we can't pop, so we
  // replace the current Roots slot with the canonical Project URL so
  // that there's still no residual Roots entry to bounce back into.
  const exitRoots = (fallbackPath) => {
    if (canPopExitRef.current) {
      nav(-1);
    } else {
      nav(fallbackPath, { replace: true });
    }
  };
  // Overflow chip is a navigation target, never a toggle. When tapped
  // we also pass the chip's angle + sway parameters so the destination
  // page can anchor its ghost limb to match, giving the illusion of
  // "continuing down the same branch" rather than teleporting into an
  // unrelated radial layout.
  //   • 1 hidden sibling → jump straight to that person's Roots.
  //   • >1 hidden       → open the next batch page (?more=k+1) centered
  //                       on the same person, showing those siblings.
  const goToOverflow = () => {
    if (hiddenCount === 0) return;
    const chip = items.find((it) => it.kind === "overflow");
    const cont = new URLSearchParams();
    if (chip) {
      // Pass the chip's actual sway direction, seed, AND curvature
      // class so the destination page's ghost limb draws the SAME
      // physical curve the chip occupied here — even now that every
      // chip has project-scoped geometry.
      cont.set("fa", chip.angle.toFixed(4));
      cont.set("fd", String(swayDirFor(chip.id)));
      cont.set("fs", swaySeedFor(chip.id).toFixed(4));
      cont.set("fm", arcMagFor(chip.id).toFixed(4));
    }
    if (hiddenCount === 1) {
      navInRoots(`/roots/${project.id}/${hiddenInvitees[0].personId}?${cont.toString()}`);
      return;
    }
    const base = isSteward
      ? `/roots/${project.id}`
      : `/roots/${project.id}/${centeredId}`;
    cont.set("more", String(moreLevel + 1));
    navInRoots(`${base}?${cont.toString()}`);
  };

  const totalDownstream = 1 + branchSize(centeredId, data.invitations, committedIds, project.id, stewardId);
  const branchesCount = allInvitees.length;

  return (
    <div style={{ paddingBottom: 24 }}>
      <div
        className="c-detail-topbar"
        style={{ display: "grid", gridTemplateColumns: "40px 1fr 40px", alignItems: "center", paddingTop: 16 }}
      >
        <button className="c-icon-btn" onClick={goBack} data-testid="roots-back">
          <ArrowLeft size={20} />
        </button>
        <div
          style={{
            textAlign: "center",
            fontFamily: "Georgia, serif",
            fontSize: 15,
            fontWeight: 700,
            color: "var(--c-on-surface-2)",
          }}
        >
          <span style={{ marginRight: 6 }}>{project.emoji}</span>
          {project.title}
        </div>
        <div />
      </div>

      <div style={{ padding: "18px 20px 8px" }}>
        {isBundleView ? (
          <>
            <div
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                fontSize: 12,
                color: "var(--c-muted)",
                fontWeight: 600,
              }}
              data-testid="roots-bundle-label"
            >
              More of {centeredPerson.name}'s invites
            </div>
            <div
              style={{
                fontFamily: "Georgia, serif",
                fontStyle: "italic",
                fontSize: 15,
                color: "var(--c-on-surface-2)",
                marginTop: 6,
                lineHeight: "20px",
              }}
            >
              {hiddenCount > 0
                ? `Showing ${shown.length} more — tap the fold to close.`
                : `Showing ${shown.length} more.`}
            </div>
          </>
        ) : isSteward ? (
          <>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 700, color: "var(--c-on-surface)" }}>
              🌱 Roots
            </div>
            <div
              style={{
                fontFamily: "Georgia, serif",
                fontStyle: "italic",
                fontSize: 15,
                color: "var(--c-on-surface-2)",
                marginTop: 4,
                lineHeight: "20px",
              }}
            >
              Every invitation that helped bring this project together.
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                fontSize: 12,
                color: "var(--c-muted)",
                fontWeight: 600,
              }}
            >
              Following
            </div>
            <div
              style={{
                fontFamily: "Georgia, serif",
                fontSize: 30,
                fontWeight: 700,
                color: "var(--c-on-surface)",
                lineHeight: "34px",
              }}
              data-testid="roots-centered-name"
            >
              {centeredPerson.name}
            </div>
            {parentPerson ? (
              <div style={{ fontSize: 14, color: "var(--c-on-surface-2)", marginTop: 4 }}>
                Invited by {parentPerson.name}
              </div>
            ) : null}
          </>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "0 8px",
          minHeight: CANVAS_SIZE + 20,
        }}
      >
        <div
          style={{ position: "relative", width: CANVAS_SIZE, height: CANVAS_SIZE }}
          data-testid="roots-canvas"
          data-tour="roots-canvas"
        >
          <svg
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            style={{ position: "absolute", inset: 0 }}
            data-testid="roots-svg"
          >
            {/* Curved dashed ghost limb — mirror of the child's outgoing
                branch on the parent's page. Same edge seed + sway, so
                it reads as one continuous branch seen from either end. */}
            {parentPlaced && ghostPath ? (
              <path
                d={ghostPath}
                stroke="#B7BEAA"
                strokeWidth={1.6}
                strokeDasharray="5 4"
                strokeLinecap="round"
                fill="none"
                data-testid="roots-parent-line"
              />
            ) : null}
            {/* Filled tapered branches from center to each direct invitee */}
            {branches.map((b) => (
              <path key={`br-${b.id}`} d={b.d} fill={SAGE} opacity={0.9} className="rt-anim-grow" />
            ))}
            {/* Small scattered leaves along each branch */}
            {branches.map((b, bi) =>
              b.leaves.map((lf, li) => (
                <g
                  key={`lf-${bi}-${li}`}
                  transform={`translate(${lf.x} ${lf.y}) rotate(${(lf.rot * 180) / Math.PI})`}
                >
                  <path d={leafShape(lf.size)} fill={SAGE} opacity={lf.opacity} />
                </g>
              )),
            )}
            {/* Terminal twigs past each invitee, gestures of "more life" */}
            {twigs.map((t, i) => (
              <path
                key={`tw-${i}`}
                d={t.d}
                stroke={SAGE_LIGHT}
                strokeWidth={1.4}
                fill="none"
                strokeLinecap="round"
                opacity={0.65}
              />
            ))}
          </svg>

          {/* Center node.
              • Normal page: the real centered person (name + initial).
              • Bundle page: an anonymous "expanded bundle" placeholder
                — no name, no initial, no navigation target. It exists
                only to anchor the visual composition. */}
          <div
            className="rt-node-wrap"
            style={{
              left: CENTER,
              top: CENTER - CENTER_R,
              transform: "translateX(-50%)",
            }}
            data-testid={isBundleView ? "root-node-bundle" : `root-node-${centeredId}`}
          >
            {isBundleView ? (
              <div
                className="rt-node rt-node-bundle-center"
                style={{ width: CENTER_R * 2, height: CENTER_R * 2, borderRadius: CENTER_R }}
                aria-hidden="true"
              />
            ) : (
              <>
                <div
                  className={
                    "rt-node" +
                    (centeredId === user.personId ? " rt-node-me" : "") +
                    " rt-node-center"
                  }
                  style={{ width: CENTER_R * 2, height: CENTER_R * 2, borderRadius: CENTER_R, fontSize: 17 }}
                >
                  <span className="rt-initial">
                    {centeredPerson.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="rt-name">{centeredPerson.name}</div>
              </>
            )}
          </div>

          {/* Faded parent node — non-interactive back-navigation cue.
              On a normal page it labels the true invitation parent and
              tapping walks up the chain. On a bundle page it labels
              the previous page's centered person (e.g. Avery) and
              tapping collapses one pagination level. */}
          {parentPlaced && visualParentPerson ? (
            <button
              className="rt-node-wrap rt-node-btn rt-node-parent-wrap"
              style={{
                left: parentPlaced.x,
                top: parentPlaced.y - NODE_R,
                transform: "translateX(-50%)",
              }}
              onClick={goToParent}
              aria-label={
                isBundleView
                  ? `Close bundle, back to ${visualParentPerson.name}`
                  : `Back to ${visualParentPerson.name}`
              }
              data-testid="roots-parent-node"
            >
              <div
                className="rt-node rt-node-parent"
                style={{ width: NODE_R * 2, height: NODE_R * 2, borderRadius: NODE_R }}
              >
                <span className="rt-initial">{visualParentPerson.name.charAt(0).toUpperCase()}</span>
              </div>
              <div className="rt-name rt-name-muted">{visualParentPerson.name}</div>
            </button>
          ) : null}

          {/* Direct invitees + overflow */}
          {items.map((it) => {
            const nodeR = NODE_R;
            const isOverflow = it.kind === "overflow";
            const isMe = !isOverflow && it.personId === user.personId;
            const initial = isOverflow ? `+${it.count}` : it.person.name.charAt(0).toUpperCase();
            const label = isOverflow ? "more" : it.person.name;
            const size = isOverflow ? 0 : it.size;
            return (
              <button
                key={`n-${it.id}`}
                className={"rt-node-wrap rt-node-btn"}
                style={{
                  left: it.x,
                  top: it.y - nodeR,
                  transform: "translateX(-50%)",
                }}
                onClick={() => (isOverflow ? goToOverflow() : goToInvitee(it.personId))}
                aria-label={
                  isOverflow
                    ? hiddenCount === 1
                      ? `Follow ${hiddenInvitees[0].person.name}`
                      : `See ${it.count} more direct invitees`
                    : `Follow ${label}`
                }
                data-testid={isOverflow ? "root-node-overflow" : `root-node-${it.personId}`}
              >
                <div
                  className={
                    "rt-node" +
                    (isMe ? " rt-node-me" : "") +
                    (isOverflow ? " rt-node-overflow" : "")
                  }
                  style={{ width: nodeR * 2, height: nodeR * 2, borderRadius: nodeR, fontSize: 15 }}
                >
                  <span className={isOverflow ? "rt-initial-plus" : "rt-initial"}>{initial}</span>
                </div>
                {label ? (
                  <div
                    className={`rt-label-pack rt-label-${it.labelPos || "below"}`}
                    style={
                      it.labelPos === "below-outward"
                        ? { transform: `translateX(-50%) translate(${it.labelOffsetX || 0}px, ${it.labelOffsetY || 0}px)` }
                        : undefined
                    }
                  >
                    <div className="rt-name">{label}</div>
                    {size > 0 ? <div className="rt-count">+{size}</div> : null}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          textAlign: "center",
          color: "var(--c-muted)",
          fontSize: 13,
          padding: "6px 20px 12px",
          letterSpacing: 0.3,
        }}
      >
        {totalDownstream} committed  ·  {branchesCount}{" "}
        {branchesCount === 1 ? "branch" : "branches"}
      </div>
    </div>
  );
}
